import { HttpErrorResponse } from '@angular/common/http';
import { DestroyRef, Injectable, InjectionToken, NgZone, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { CookieService } from 'ngx-cookie-service';
import posthog, { Properties } from 'posthog-js';
import { filter } from 'rxjs/operators';
import { environment } from 'environments/environment';
import { asProblemDetails, classifyHttpFailure, isLoginRequiredExceptionEvent, normalizeTelemetryPath, sanitizeDiagnosticText } from './posthog-diagnostics';
import { ResolvedUserContext, UserContextService } from './user-context.service';
import { DeploymentMetadata, VersionCheckService } from './version-check.service';

export type StoreGroupContext = {
    locID?: number;
    customerName?: string;
};

export type PosthogEventProperties = Properties;

export const POSTHOG_CLIENT = new InjectionToken<typeof posthog>('PostHog client', {
    factory: () => posthog
});

@Injectable({ providedIn: 'root' })
export class PosthogService {
    private readonly destroyRef = inject(DestroyRef);
    private readonly ngZone = inject(NgZone);
    private readonly posthog = inject(POSTHOG_CLIENT);
    private readonly router = inject(Router);
    private readonly authService = inject(AuthService);
    private readonly userContextService = inject(UserContextService);
    private readonly cookieService = inject(CookieService);
    private readonly versionCheckService = inject(VersionCheckService);

    private enabled = false;
    private identifiedUserId: string | null = null;
    private activeStoreNumber: string | null = null;
    private deploymentMetadata?: DeploymentMetadata;
    private apiRelease: string | null = null;

    constructor() {
        if (!environment.posthogKey) {
            return;
        }

        this.enabled = true;

        this.ngZone.runOutsideAngular(() => {
            const apiHostname = new URL(environment.backendServerUrl || window.location.origin, window.location.origin).hostname;

            this.posthog.init(environment.posthogKey, {
                api_host: environment.posthogHost,
                ui_host: environment.posthogUiHost,
                tracing_headers: [apiHostname],
                defaults: '2026-01-30',
                person_profiles: 'identified_only',
                // Feature flags come from the API's FeatureManagement configuration.
                // Evaluate only the internal flags that target PostHog surveys.
                advanced_only_evaluate_survey_feature_flags: true,
                enable_recording_console_log: environment.posthogEnableConsoleLog,
                session_recording: {
                    maskAllInputs: false,
                    blockClass: 'ph-no-capture',
                    maskTextClass: 'ph-mask',
                    ignoreClass: 'ph-ignore-input'
                },
                capture_exceptions: false,
                before_send: (event) => (isLoginRequiredExceptionEvent(event) ? null : event)
            });
            this.registerSuperProperties();
            this.syncStoreContextFromCookies();
        });

        this.authService.isAuthenticated$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((isAuthenticated) => {
            if (!isAuthenticated) {
                this.reset();
            }
        });

        this.userContextService.userContext$
            .pipe(
                filter((userInfo): userInfo is ResolvedUserContext & { UserId: string } => !!userInfo.UserId),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((userInfo) => this.identifyUser(userInfo));

        this.versionCheckService.deploymentMetadata$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((metadata) => {
            this.deploymentMetadata = metadata;
            this.ngZone.runOutsideAngular(() => this.registerSuperProperties());
        });
    }

    /** Captures explicit product events for dashboards and funnels. */
    capture(eventName: string, properties: PosthogEventProperties = {}): void {
        if (!this.enabled) {
            return;
        }

        this.ngZone.runOutsideAngular(() => {
            this.posthog.capture(eventName, properties);
        });
    }

    /** Sends an exception to PostHog error tracking. */
    captureException(error: unknown, properties: PosthogEventProperties = {}): void {
        if (!this.enabled) {
            return;
        }

        const normalizedError = this.normalizeException(error);

        this.ngZone.runOutsideAngular(() => {
            this.posthog.captureException(normalizedError, {
                ...this.getExceptionContext(),
                ...this.getExceptionProperties(error),
                ...properties
            });
        });
    }

    /** Captures a safe, low-cardinality summary after an API request exhausts retries. */
    captureApiFailure(error: HttpErrorResponse, request: { method: string; url: string }, durationMs: number, retryAttempts: number): void {
        const endpoint = normalizeTelemetryPath(request.url);
        if (!endpoint?.startsWith('/api/')) {
            return;
        }

        const problemDetails = asProblemDetails(error.error);
        this.capture('apiRequestFailed', {
            method: request.method,
            endpoint,
            status: error.status,
            failureType: classifyHttpFailure(error.status, error.statusText),
            durationMs,
            retryAttempts,
            attemptCount: retryAttempts + 1,
            problemTitle: problemDetails?.title ? sanitizeDiagnosticText(problemDetails.title) : undefined,
            problemTraceId: problemDetails?.traceId
        });
    }

    /** Clears identity and store groups so the next session does not inherit this user. */
    reset(): void {
        if (!this.enabled) {
            return;
        }

        this.ngZone.runOutsideAngular(() => {
            this.posthog.reset();
            this.registerSuperProperties();
        });
        this.identifiedUserId = null;
        this.activeStoreNumber = null;
    }

    /** Associates the session with customer and store groups in PostHog. */
    setStoreContext(storeNumber: string, context: StoreGroupContext = {}): void {
        const normalizedStoreNumber = storeNumber?.trim();
        if (!this.enabled || !normalizedStoreNumber || normalizedStoreNumber === this.activeStoreNumber) {
            return;
        }

        this.activeStoreNumber = normalizedStoreNumber;
        const customerId = this.parseCustomerId(normalizedStoreNumber) ?? normalizedStoreNumber;
        const customerProperties: Record<string, string> = {};
        const storeProperties: Record<string, string> = { customerId };

        if (context.customerName) {
            customerProperties.name = context.customerName;
        }

        if (context.locID != null) {
            storeProperties.locID = String(context.locID);
        }

        this.ngZone.runOutsideAngular(() => {
            this.posthog.group('customer', customerId, customerProperties);
            this.posthog.group('store', normalizedStoreNumber, storeProperties);
            this.posthog.register({
                customerId,
                storeNumber: normalizedStoreNumber
            });
        });
    }

    /** Reads storeNumber/locID cookies and applies PostHog store groups when present. */
    syncStoreContextFromCookies(): void {
        const storeNumber = this.cookieService.get('storeNumber')?.trim();
        if (!storeNumber) {
            return;
        }

        const locIdRaw = this.cookieService.get('locID')?.trim();
        const parsedLocId = locIdRaw ? Number(locIdRaw) : undefined;

        this.setStoreContext(storeNumber, {
            locID: parsedLocId != null && Number.isFinite(parsedLocId) ? parsedLocId : undefined
        });
    }

    /** Sends the Store API's release (from apiReleaseInterceptor) with every later event as `api_release`. */
    setApiRelease(release: string): void {
        if (!this.enabled || release === this.apiRelease) {
            return;
        }

        this.apiRelease = release;
        this.posthog.register({ api_release: release });
    }

    private registerSuperProperties(): void {
        this.posthog.unregister('stm_environment');
        this.posthog.unregister('release');
        this.posthog.unregister('commitSha');

        const properties: Record<string, string> = { app: 'STM.Store' };
        if (this.deploymentMetadata) {
            properties['release'] = this.deploymentMetadata.release;

            if (this.deploymentMetadata.commitSha) {
                properties['commitSha'] = this.deploymentMetadata.commitSha;
            }
        }

        if (this.apiRelease) {
            properties['api_release'] = this.apiRelease;
        }

        this.posthog.register(properties);
    }

    private getExceptionContext(): PosthogEventProperties {
        const context: PosthogEventProperties = { app: 'STM.Store' };

        const route = normalizeTelemetryPath(this.router.url);
        if (route) {
            context.route = route;
        }

        const storeNumber = this.cookieService.get('storeNumber')?.trim();
        if (storeNumber) {
            context.storeNumber = storeNumber;

            const customerId = this.parseCustomerId(storeNumber);
            if (customerId) {
                context.customerId = customerId;
            }
        }

        return context;
    }

    private identifyUser(userInfo: ResolvedUserContext): void {
        if (!this.enabled || !userInfo.UserId || userInfo.UserId === this.identifiedUserId) {
            return;
        }

        this.identifiedUserId = userInfo.UserId;

        const personProperties: Record<string, string> = {};

        if (userInfo.Email) {
            personProperties.email = userInfo.Email;
        }

        if (userInfo.fullname) {
            personProperties.name = userInfo.fullname;
        }

        if (userInfo.CustomerId) {
            personProperties.customerId = String(userInfo.CustomerId);
        }

        this.ngZone.runOutsideAngular(() => {
            this.posthog.identify(userInfo.UserId!, personProperties);
            this.syncStoreContextFromCookies();
        });
    }

    private parseCustomerId(storeNumber: string): string | undefined {
        const separatorIndex = storeNumber.indexOf('-');
        if (separatorIndex <= 0) {
            return undefined;
        }

        return storeNumber.slice(0, separatorIndex);
    }

    private normalizeException(error: unknown): unknown {
        if (error instanceof HttpErrorResponse) {
            const details = asProblemDetails(error.error);
            const message = details?.title ? `${details.title} (HTTP ${error.status})` : `HTTP ${error.status} ${error.statusText || 'request failed'}`;
            return this.createSanitizedError(message, error.name);
        }

        if (error instanceof Error) {
            return this.createSanitizedError(error.message, error.name, error.stack);
        }

        const wrappedError = this.unwrapError(error);
        if (wrappedError instanceof Error) {
            return this.createSanitizedError(wrappedError.message, wrappedError.name, wrappedError.stack);
        }

        const problemDetails = asProblemDetails(wrappedError);
        if (problemDetails) {
            return this.createSanitizedError(problemDetails.status ? `${problemDetails.title} (HTTP ${problemDetails.status})` : problemDetails.title, 'ProblemDetails');
        }

        if (typeof wrappedError === 'string') {
            return this.createSanitizedError(wrappedError);
        }

        return this.createSanitizedError(this.describeObject(wrappedError));
    }

    private getExceptionProperties(error: unknown): PosthogEventProperties {
        const httpError = error instanceof HttpErrorResponse ? error : undefined;
        const details = asProblemDetails(httpError?.error ?? this.unwrapError(error));

        return {
            httpStatus: httpError?.status,
            httpEndpoint: normalizeTelemetryPath(httpError?.url),
            failureType: httpError ? classifyHttpFailure(httpError.status, httpError.statusText) : undefined,
            problemStatus: details?.status,
            problemTitle: details?.title ? sanitizeDiagnosticText(details.title) : undefined,
            problemTraceId: details?.traceId,
            problemInstance: normalizeTelemetryPath(details?.instance)
        };
    }

    private unwrapError(error: unknown): unknown {
        if (this.isObjectRecord(error) && 'error' in error) {
            return error.error;
        }

        if (this.isObjectRecord(error) && 'rejection' in error) {
            return error.rejection;
        }

        return error;
    }

    private isObjectRecord(value: unknown): value is Record<string, unknown> {
        return typeof value === 'object' && value !== null;
    }

    private describeObject(value: unknown): string {
        if (!this.isObjectRecord(value)) {
            return String(value);
        }

        const keys = Object.keys(value).join(', ');
        return keys ? `Object captured as exception with keys: ${keys}` : 'Object captured as exception';
    }

    private createSanitizedError(message: string, name = 'Error', stack?: string): Error {
        const sanitizedError = new Error(sanitizeDiagnosticText(message));
        sanitizedError.name = name;
        if (stack) {
            sanitizedError.stack = sanitizeDiagnosticText(stack);
        }
        return sanitizedError;
    }
}
